import React, { useState } from "react";
import { useNotes, useCourses } from "../hooks/useStudyOS";
import { deleteNote, uploadNoteFile, addNote, deleteNoteFile } from "../lib/db";
import AIScreen from "./AIScreen";

export default function NotesScreen() {
  const { notes } = useNotes();
  const { courses, getCourse } = useCourses();
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [aiNote, setAiNote] = useState(null);

  const filtered = notes.filter(n =>
    n.title.toLowerCase().includes(search.toLowerCase()) ||
    n.content?.toLowerCase().includes(search.toLowerCase())
  );

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const valid = selected.filter(f => {
      const isValidType = f.type.startsWith("image/") || f.type === "application/pdf";
      const isValidSize = f.size <= 10 * 1024 * 1024;
      return isValidType && isValidSize;
    });
    if (valid.length !== selected.length) setError("Only images and PDFs under 10MB are allowed.");
    setFiles(valid);
  };

  const handleSaveNote = async () => {
    if (!form.title) { setError("Title is required"); return; }
    setUploading(true); setError("");
    try {
      const attachments = [];
      for (const file of files) {
        const result = await uploadNoteFile(file, (progress) => setUploadProgress(progress));
        attachments.push(result);
      }
      await addNote({ title: form.title, content: form.content || "", courseId: form.courseId || null, attachments });
      setForm({}); setFiles([]); setShowModal(false);
    } catch (err) {
      setError("Upload failed. Please try again.");
    }
    setUploading(false); setUploadProgress(0);
  };

  const handleDelete = async (note) => {
    if (!window.confirm("Delete this note?")) return;
    for (const att of note.attachments || []) {
      if (att.path) await deleteNoteFile(att.path);
    }
    await deleteNote(note.id);
    setViewing(null);
  };

  // ── NOTE VIEWER ──
  if (viewing) {
    const note = notes.find(n => n.id === viewing);
    if (!note) { setViewing(null); return null; }
    const course = getCourse(note.courseId);
    return (
      <div style={styles.viewer}>
        <div style={styles.viewerTop}>
          <button style={styles.backBtn} onClick={() => setViewing(null)}>← Back</button>
          <div style={{display:"flex", gap:"8px"}}>
            <button style={styles.aiBtn} onClick={() => setAiNote(note)}>🤖 Study with AI</button>
            <button style={styles.deleteBtn} onClick={() => handleDelete(note)}>Delete</button>
          </div>
        </div>
        <div style={styles.viewerTitle}>{note.title}</div>
        <div style={styles.viewerMeta}>
          {course ? `${course.name} · ` : ""}
          {note.createdAt?.toDate?.()?.toLocaleDateString() || ""}
        </div>
        <div style={styles.viewerContent}>{note.content || "(No content)"}</div>

        {note.attachments?.length > 0 && (
          <div style={styles.attachSection}>
            <div style={styles.attachLabel}>Attachments</div>
            <div style={styles.attachGrid}>
              {note.attachments.map((att, i) => (
                <div key={i} style={styles.attachItem}>
                  {att.type?.startsWith("image/") ? (
                    <a href={att.url} target="_blank" rel="noreferrer">
                      <img src={att.url} alt={att.name}
                        style={{width:"100%", height:"120px", objectFit:"cover", borderRadius:"8px"}}/>
                    </a>
                  ) : (
                    <a href={att.url} target="_blank" rel="noreferrer" style={styles.pdfLink}>
                      <span style={{fontSize:"24px"}}>📄</span>
                      <span style={styles.pdfName}>{att.name}</span>
                      <span style={styles.pdfSize}>{Math.round(att.size/1024)}KB</span>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {aiNote && <AIScreen note={aiNote} onClose={() => setAiNote(null)} />}
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.searchWrap}>
        <span style={styles.searchIcon}>🔍</span>
        <input className="search-input" placeholder="Search notes..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <button style={styles.addNoteBtn} onClick={() => { setShowModal(true); setForm({}); setFiles([]); setError(""); }}>
        + New note
      </button>

      {notes.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📝</div>
          <div style={styles.emptyTitle}>No notes yet</div>
          <div style={styles.emptyDesc}>Tap "+ New note" to write your first note. You can attach images and PDFs.</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>🔍</div>
          <div style={styles.emptyTitle}>No results</div>
          <div style={styles.emptyDesc}>Try a different search term.</div>
        </div>
      ) : (
        filtered.map(n => {
          const course = getCourse(n.courseId);
          const preview = n.content?.substring(0, 120) + (n.content?.length > 120 ? "…" : "");
          return (
            <div key={n.id} style={styles.noteCard} onClick={() => setViewing(n.id)}>
              <div style={styles.noteTitle}>{n.title}</div>
              <div style={styles.notePreview}>{preview || "(Empty note)"}</div>
              {n.attachments?.length > 0 && (
                <div style={styles.attachBadge}>📎 {n.attachments.length} attachment{n.attachments.length > 1 ? "s" : ""}</div>
              )}
              <div style={styles.noteFooter}>
                {course ? (
                  <span style={{...styles.noteTag, background:`${course.color}20`, color: course.color}}>{course.name}</span>
                ) : <span />}
                <span style={styles.noteDate}>{n.createdAt?.toDate?.()?.toLocaleDateString() || ""}</span>
              </div>
            </div>
          );
        })
      )}

      {showModal && (
        <div style={styles.overlay} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={styles.modalBox}>
            <div style={styles.handle} />
            <div style={styles.modalTitle}>New note</div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Title *</label>
              <input style={styles.input} placeholder="Note title"
                value={form.title||""} onChange={e => setForm({...form, title: e.target.value})} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Content</label>
              <textarea style={{...styles.input, minHeight:"100px", resize:"vertical"}}
                placeholder="Write your note here..."
                value={form.content||""} onChange={e => setForm({...form, content: e.target.value})} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Course</label>
              <select style={styles.input} value={form.courseId||""}
                onChange={e => setForm({...form, courseId: e.target.value})}>
                <option value="">No course</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Attachments (images or PDFs, max 10MB each)</label>
              <label style={styles.filePickerBtn}>
                📎 Choose files
                <input type="file" multiple accept="image/*,.pdf"
                  style={{display:"none"}} onChange={handleFileSelect} />
              </label>
              {files.length > 0 && (
                <div style={styles.fileList}>
                  {files.map((f, i) => (
                    <div key={i} style={styles.fileItem}>
                      <span style={styles.fileName}>{f.name}</span>
                      <span style={styles.fileSize}>{Math.round(f.size/1024)}KB</span>
                      <button style={styles.fileRemove}
                        onClick={() => setFiles(files.filter((_, idx) => idx !== i))}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {uploading && (
              <div style={styles.progressWrap}>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width:`${uploadProgress}%`}} />
                </div>
                <div style={styles.progressText}>Uploading... {uploadProgress}%</div>
              </div>
            )}
            {error && <div style={styles.errorText}>{error}</div>}
            <button style={{...styles.submitBtn, opacity: uploading ? 0.7 : 1}}
              onClick={handleSaveNote} disabled={uploading}>
              {uploading ? "Saving..." : "Save note"}
            </button>
          </div>
        </div>
      )}

      {aiNote && <AIScreen note={aiNote} onClose={() => setAiNote(null)} />}
    </div>
  );
}

const styles = {
  wrap:          { padding:"20px 20px 0" },
  searchWrap:    { position:"relative", marginBottom:"12px" },
  searchIcon:    { position:"absolute", left:"12px", top:"50%", transform:"translateY(-50%)", fontSize:"14px" },
  addNoteBtn:    { width:"100%", padding:"12px", background:"var(--accent-bg)", border:"1px solid var(--accent-border)", borderRadius:"12px", color:"var(--accent2)", fontFamily:"inherit", fontSize:"14px", fontWeight:600, cursor:"pointer", marginBottom:"16px", textAlign:"center" },
  noteCard:      { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"16px", padding:"16px", marginBottom:"8px", cursor:"pointer" },
  noteTitle:     { fontSize:"15px", fontWeight:600, color:"var(--text)", marginBottom:"5px" },
  notePreview:   { fontSize:"13px", color:"var(--text2)", lineHeight:1.5, marginBottom:"8px" },
  attachBadge:   { fontSize:"11px", color:"var(--accent2)", marginBottom:"8px", fontWeight:500 },
  noteFooter:    { display:"flex", justifyContent:"space-between", alignItems:"center" },
  noteTag:       { fontSize:"11px", fontWeight:500, padding:"3px 9px", borderRadius:"20px" },
  noteDate:      { fontSize:"11px", color:"var(--text3)" },
  empty:         { textAlign:"center", padding:"48px 20px" },
  emptyIcon:     { fontSize:"36px", marginBottom:"10px" },
  emptyTitle:    { fontSize:"16px", fontWeight:600, color:"var(--text)", marginBottom:"6px" },
  emptyDesc:     { fontSize:"13px", color:"var(--text2)" },
  viewer:        { padding:"20px", minHeight:"100vh", background:"var(--bg)" },
  viewerTop:     { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px" },
  backBtn:       { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"8px", padding:"8px 14px", fontFamily:"inherit", fontSize:"13px", color:"var(--text)", cursor:"pointer" },
  aiBtn:         { background:"var(--accent-bg)", border:"1px solid var(--accent-border)", borderRadius:"8px", padding:"8px 14px", fontFamily:"inherit", fontSize:"13px", color:"var(--accent2)", cursor:"pointer", fontWeight:500 },
  deleteBtn:     { background:"var(--red-bg)", border:"1px solid rgba(240,93,93,.2)", borderRadius:"8px", padding:"8px 14px", fontFamily:"inherit", fontSize:"13px", color:"var(--red)", cursor:"pointer" },
  viewerTitle:   { fontSize:"24px", fontWeight:700, color:"var(--text)", marginBottom:"8px", letterSpacing:"-.5px" },
  viewerMeta:    { fontSize:"12px", color:"var(--text3)", marginBottom:"20px" },
  viewerContent: { fontSize:"15px", color:"var(--text2)", lineHeight:1.7, whiteSpace:"pre-wrap", wordBreak:"break-word", marginBottom:"24px" },
  attachSection: { marginTop:"20px" },
  attachLabel:   { fontSize:"12px", fontWeight:600, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"10px" },
  attachGrid:    { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:"10px" },
  attachItem:    { borderRadius:"10px", overflow:"hidden" },
  pdfLink:       { display:"flex", flexDirection:"column", alignItems:"center", gap:"6px", padding:"16px", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"10px", textDecoration:"none" },
  pdfName:       { fontSize:"12px", color:"var(--text)", fontWeight:500, textAlign:"center", wordBreak:"break-all" },
  pdfSize:       { fontSize:"11px", color:"var(--text3)" },
  overlay:       { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:200 },
  modalBox:      { background:"var(--modal-bg)", borderRadius:"24px 24px 0 0", padding:"20px 20px 40px", width:"100%", maxWidth:"480px", maxHeight:"90vh", overflowY:"auto" },
  handle:        { width:"38px", height:"4px", background:"var(--surface2)", borderRadius:"2px", margin:"0 auto 18px" },
  modalTitle:    { fontSize:"18px", fontWeight:700, color:"var(--text)", marginBottom:"18px" },
  formGroup:     { marginBottom:"14px" },
  label:         { fontSize:"12px", fontWeight:600, color:"var(--text2)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"6px", display:"block" },
  input:         { width:"100%", padding:"12px 14px", background:"var(--input-bg)", border:"1px solid var(--border2)", borderRadius:"8px", fontFamily:"inherit", fontSize:"14px", color:"var(--text)", outline:"none" },
  filePickerBtn: { display:"inline-flex", alignItems:"center", gap:"8px", padding:"10px 16px", background:"var(--surface2)", border:"1px solid var(--border2)", borderRadius:"8px", cursor:"pointer", fontSize:"13px", fontWeight:500, color:"var(--text)", marginTop:"6px" },
  fileList:      { marginTop:"10px", display:"flex", flexDirection:"column", gap:"6px" },
  fileItem:      { display:"flex", alignItems:"center", gap:"8px", padding:"8px 12px", background:"var(--surface2)", borderRadius:"8px" },
  fileName:      { flex:1, fontSize:"12px", color:"var(--text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  fileSize:      { fontSize:"11px", color:"var(--text3)", flexShrink:0 },
  fileRemove:    { background:"none", border:"none", color:"var(--text3)", fontSize:"16px", cursor:"pointer", padding:"0 2px", flexShrink:0 },
  progressWrap:  { marginBottom:"12px" },
  progressBar:   { height:"6px", background:"var(--surface2)", borderRadius:"3px", overflow:"hidden", marginBottom:"6px" },
  progressFill:  { height:"100%", background:"var(--accent)", borderRadius:"3px", transition:"width .3s ease" },
  progressText:  { fontSize:"12px", color:"var(--text2)", textAlign:"center" },
  errorText:     { fontSize:"13px", color:"var(--red)", marginBottom:"10px" },
  submitBtn:     { width:"100%", padding:"14px", background:"var(--accent)", border:"none", borderRadius:"12px", fontFamily:"inherit", fontSize:"15px", fontWeight:600, color:"#fff", cursor:"pointer", marginTop:"4px" },
};